/**
 * PKCE (Proof Key for Code Exchange) utilities for OAuth 2.0
 * RFC 7636: https://datatracker.ietf.org/doc/html/rfc7636
 */

/**
 * Base64 URL encoding (without padding)
 */
function base64urlEncode(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer)
  let binary = ''
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i])
  }

  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '')
}

/**
 * Generate a cryptographically random code verifier
 * Length: 43-128 characters (using unreserved URI characters)
 *
 * @returns A random code verifier string (43 characters)
 */
export function generateCodeVerifier(): string {
  const array = new Uint8Array(32)
  crypto.getRandomValues(array)

  return base64urlEncode(array.buffer)
}

/**
 * Generate a code challenge from a code verifier using SHA-256
 *
 * @param codeVerifier - The code verifier to hash
 * @returns A promise that resolves to the base64url-encoded SHA-256 hash
 */
export async function generateCodeChallenge(
  codeVerifier: string
): Promise<string> {
  const encoder = new TextEncoder()
  const data = encoder.encode(codeVerifier)
  const digest = await crypto.subtle.digest('SHA-256', data)

  return base64urlEncode(digest)
}

/**
 * Generate both code verifier and code challenge
 *
 * @returns A promise that resolves to an object containing both values
 */
export async function generatePKCEPair(): Promise<{
  codeVerifier: string
  codeChallenge: string
}> {
  const codeVerifier = generateCodeVerifier()
  const codeChallenge = await generateCodeChallenge(codeVerifier)

  return { codeVerifier, codeChallenge }
}
